/**
 * Shared retry policy for the PV attachment upload helpers.
 *
 * The upload endpoints (POST /post_images_pv etc.) can fail with different
 * statuses that demand different client behaviour:
 *
 *   FAIL FAST (server explicitly refuses — retrying is pointless):
 *     400  Bad request (e.g. missing PV code, invalid form)
 *     401/403/404  Auth / not found
 *     413  Payload too large (file/count over the server cap)
 *     429  Too many requests (rate limited; server message includes the wait)
 *     other 4xx, 501/505+ (permanent server errors)
 *
 *   RETRY WITH BACKOFF (transient — may clear up):
 *     0    Network failure / connection dropped
 *     500  Internal error
 *     502  Bad gateway
 *     503  Server busy / memory limit (temporary by design)
 *     504  Gateway timeout
 *     507  Disk full (space may free up)
 */
export const UPLOAD_RETRYABLE_STATUSES = new Set([0, 500, 502, 503, 504, 507]);

export const isUploadRetryable = (status) =>
  UPLOAD_RETRYABLE_STATUSES.has(status);

/**
 * Best-effort human-readable message for a failed upload XHR.
 * Prefers the server's own message (e.g. "Upload too large (310 MB)…" or
 * "Too many upload requests. Please wait 59s and try again."), then falls
 * back to a status-aware generic message.
 */
export const getUploadErrorMessage = (xhr) => {
  let serverMessage = "";
  try {
    const parsed = JSON.parse(xhr.responseText);
    serverMessage = (parsed && parsed.message) || "";
  } catch (err) {
    /* non-JSON response — use fallbacks below */
  }
  if (serverMessage) return serverMessage;
  if (xhr.status === 413) {
    return "Upload too large. Please reduce the number or size of files and try again.";
  }
  if (xhr.status === 429) {
    return "Too many upload requests. Please wait a moment and try again.";
  }
  if (xhr.status === 0) {
    return "Network error - please check your connection";
  }
  return `Upload failed with status ${xhr.status}`;
};
